import { createPopper } from '@popperjs/core';
import AirDatepicker from 'air-datepicker';
import locale from 'air-datepicker/locale/en';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { isPositionWithinDOMRect } from '../utils/dom';
import Scrolling from '../utils/scrolling';
import { buildPropsHelper } from './helpers/props';

dayjs.extend(customParseFormat);

const ESCAPE_KEY_CODE = 27;
const VALUE_FORMAT = 'YYYY-MM-DD';
const INPUT_FORMATS = [
  'MMM D YYYY',
  'MMM D, YYYY',
  'MMM D,YYYY',
  'MMMM D YYYY',
  'MMMM D, YYYY',
  'MMMM D,YYYY',
  'YYYY/MM/DD',
];

export default {
  DatePicker: {
    ...buildPropsHelper(),

    mounted() {
      this.isDestroyed = false;
      this.onDateChangedCallback = this.onDateChanged.bind(this);
      this.onClickCallback = this.onClick.bind(this);
      this.onClickOutsideCallback = this.onClickOutside.bind(this);
      this.clearButtonCallback = this.clearValue.bind(this);
      this.escapePressedCallback = this.onEscapePressed.bind(this);
      this.onFocusEventCallback = this.onFocusEvent.bind(this);

      this.initialize();
    },

    updated() {
      if (this.propsChanged()) this.initialize();
    },

    destroyed() {
      this.isDestroyed = true;
      this.unregisterListeners();

      this.datepicker?.destroy();
      this.datepicker = undefined;
      this.scrolling?.enable();
    },

    getProps() {
      return {
        onBlurEvent: this.el.dataset.onBlurEvent,
        eventTarget: this.el.dataset.eventTarget,
        inputId: this.el.dataset.inputId,
        textInputId: this.el.dataset.textInputId,
        wrapperId: this.el.dataset.wrapperId,
        isEditable: this.el.dataset.editable == 'true' ? true : false,
        value: dayjs(document.getElementById(this.el.dataset.inputId).value, VALUE_FORMAT, true),
        controlScrolling: this.el.dataset.controlScrolling == 'true' ? true : false,
      };
    },

    initialize() {
      this.setProps();

      this.input = document.getElementById(this.inputId);
      this.textInput = document.getElementById(this.textInputId);
      this.wrapper = document.getElementById(this.wrapperId);

      if (this.isEditable) {
        if (!this.datepicker) {
          this.datepicker = this.initializeDatePicker();
          this.clearButton = document.getElementById(this.el.dataset.clearButtonId);
          this.registerListeners();
        }

        this.focusDateOnDatePicker(this.value);
      } else {
        if (this.datepicker) {
          this.datepicker.destroy();
          this.datepicker = undefined;
        }

        this.unregisterListeners();
      }

      this.scrolling = this.initializeScrolling();

      this.displayHumanReadableValue();
    },

    registerListeners() {
      this.textInput.addEventListener('input', this.onDateChangedCallback);
      this.el.addEventListener('click', this.onClickCallback);

      if (this.clearButton) this.clearButton.addEventListener('click', this.clearButtonCallback);

      window.addEventListener('phx:focusInput', this.onFocusEventCallback);
    },

    unregisterListeners() {
      this.textInput.removeEventListener('input', this.onDateChangedCallback);
      this.el.removeEventListener('click', this.onClickCallback);
      this.unregisterDocumentListeners();

      if (this.clearButton) this.clearButton.removeEventListener('click', this.clearButtonCallback);

      window.removeEventListener('phx:focusInput', this.onFocusEventCallback);
    },

    registerDocumentListeners() {
      document.addEventListener('click', this.onClickOutsideCallback);
      document.addEventListener('keydown', this.escapePressedCallback);
    },

    unregisterDocumentListeners() {
      document.removeEventListener('click', this.onClickOutsideCallback);
      document.removeEventListener('keydown', this.escapePressedCallback);
    },

    setValue(value) {
      const date = dayjs(value, VALUE_FORMAT, true);

      this.value = date;
      this.input.value = date.isValid() ? date.format(VALUE_FORMAT) : value;

      this.input.dispatchEvent(new Event('input', { bubbles: true }));
    },

    clearValue() {
      this.value = undefined;
      this.input.value = '';

      this.onBlur();
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
    },

    onClick(event) {
      const valueIsReference = this.textInput.value.match(/\{\{\{(.*?)\}\}\}/g);
      const isClickOnClearButton = this.clearButton && this.clearButton.contains(event.target);

      if (!isClickOnClearButton && !this.datepicker.visible && !valueIsReference) this.datepicker.show();

      event.stopPropagation();
    },

    onClickOutside(event) {
      const datepickerRect = this.datepicker.$datepicker.getBoundingClientRect();
      const isClickInDatePicker = isPositionWithinDOMRect({ x: event.clientX, y: event.clientY }, datepickerRect);

      // Check if click is on navigation buttons or inside the datepicker
      const isNavigationClick =
        event.target.closest('.air-datepicker-nav') || event.target.closest('.air-datepicker-button');

      if (isClickInDatePicker || isNavigationClick) {
        event.stopPropagation();
        return;
      }

      this.onBlur();

      if (this.datepicker.visible) {
        this.datepicker.hide();
      }

      this.textInput.dispatchEvent(new CustomEvent('refreshContent'));
    },

    onBlur() {
      if (this.onBlurEvent) {
        if (this.eventTarget) {
          const rootElement = this.el;
          const eventTarget = this.eventTarget;
          const inputName = this.input.name;
          const onBlurEvent = this.onBlurEvent;

          // delaying so that its sent after dispatch event
          setTimeout(() => {
            if (this.isDestroyed || !rootElement.isConnected) return;

            this.pushEventTo(eventTarget, onBlurEvent, {
              name: inputName,
            });
          }, 50);
        } else this.pushEvent(this.onBlurEvent, { name: this.input.name });
      }
    },

    onDateChanged(event) {
      event.stopPropagation();

      const value = event.target.value;
      const valueIsReference = value.match(/\{\{\{(.*?)\}\}\}/g);

      if (value.match(/@/)) {
        this.datepicker.hide();
      } else {
        if (!this.datepicker.visible && !valueIsReference) {
          this.datepicker.show();
        }

        this.focusDateOnDatePicker(value);
      }

      const date = dayjs(value, INPUT_FORMATS, true);

      this.setValue(date.isValid() ? date.format(VALUE_FORMAT) : value);
    },

    onEscapePressed(event) {
      if ((event.key === 'Escape' || event.keyCode === ESCAPE_KEY_CODE) && this.datepicker.visible) {
        this.datepicker.hide();
      }
    },

    focusDateOnDatePicker(value) {
      let date = dayjs(value, INPUT_FORMATS, true);

      if (!date.isValid()) return;

      this.datepicker.setFocusDate(date.format(VALUE_FORMAT));
      this.datepicker.setViewDate(date.format(VALUE_FORMAT));
    },

    onFocusEvent({ detail: { name, placement } }) {
      if (name === this.input.name) {
        this.el.click();
        this.textInput.dispatchEvent(new CustomEvent('focus', { detail: { placement } }));
      }
    },

    initializeDatePicker() {
      return new AirDatepicker(this.input, {
        locale,
        dateFormat: VALUE_FORMAT,
        onShow: () => {
          this.registerDocumentListeners();
          this.scrolling?.disable();
        },
        onHide: () => {
          this.unregisterDocumentListeners();
          this.scrolling?.enable();
        },
        onSelect: ({ date }) => {
          // Only handle actual date selections, not navigation
          if (date) {
            this.datepicker.hide();
            this.setValue(dayjs(date).format(VALUE_FORMAT));
          }
        },
        position: ({ $datepicker, done }) => {
          const popper = createPopper(this.wrapper, $datepicker, {
            placement: 'bottom-start',
            strategy: 'absolute',
            modifiers: [
              {
                name: 'flip',
                options: {
                  padding: {
                    top: 20,
                  },
                },
              },
              {
                name: 'offset',
                options: {
                  offset: ({ placement }) => {
                    if (placement.startsWith('bottom')) {
                      return [0, 10];
                    } else {
                      return [0, 20];
                    }
                  },
                },
              },
            ],
          });

          return () => {
            popper.destroy();
            done();
          };
        },
      });
    },

    displayHumanReadableValue() {
      if (this.value && this.value.isValid()) {
        this.textInput.value = this.getHumanReadableValue();
      }
    },

    getHumanReadableValue() {
      return this.value ? this.value.format('MMMM D, YYYY') : '';
    },

    initializeScrolling() {
      let wasDisabled = false;

      if (this.scrolling) {
        wasDisabled = !this.scrolling.isEnabled;

        if (wasDisabled) this.scrolling.enable();
      }

      if (!this.controlScrolling) return;

      const scrolling = new Scrolling(this.el);

      if (wasDisabled) scrolling.disable();

      return scrolling;
    },
  },
};
